import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { RecordPicker } from '../../components/RecordPicker.js';

/* ─────────────────────────────────────────────────────────────────────────
   L17.10 — Territory Master (`/admin/master-config/territories`, §35 #5).
   Lists territory configurations and captures a new one over
   `v1/m17/governance/territories`.
   ───────────────────────────────────────────────────────────────────────── */

interface TerritoryRow {
  readonly id?: string;
  readonly territoryConfigId?: string;
  readonly territoryCode?: string | null;
  readonly territoryName?: string | null;
  readonly hierarchyLevel?: string;
  readonly parentTerritoryCode?: string | null;
  readonly parentTerritoryName?: string | null;
  readonly status?: string;
  readonly recordStatusCode?: string;
}

/** A territory of the master, as /v1/territories lists it. */
interface TerritoryOption {
  readonly territoryId: string;
  readonly territoryCode?: string | null;
  readonly territoryName?: string | null;
}

const named = (code?: string | null, name?: string | null): string =>
  [code, name].filter(Boolean).join(' — ') || '—';

export function M17TerritoryConfigPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<TerritoryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [territoryId, setTerritoryId] = useState('');
  const [hierarchyLevel, setHierarchyLevel] = useState('');
  const [parentTerritoryId, setParentTerritoryId] = useState('');
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    setRows(null);
    apiRequest<{ data: TerritoryRow[] }>('/v1/m17/governance/territories?page=1&limit=50', { token })
      .then((r) => { if (alive) setRows(r.data ?? []); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token, reloadKey]);

  async function onCreate(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setNotice(null);
    if (!territoryId) { setError(t('m17.territoryConfig.errTerritoryId')); return; }
    if (!hierarchyLevel) { setError(t('m17.territoryConfig.errHierarchy')); return; }
    setSaving(true);
    try {
      await apiRequest('/v1/m17/governance/territories', {
        method: 'POST', token,
        body: {
          territoryId,
          hierarchyLevel,
          ...(parentTerritoryId ? { parentTerritoryId } : {}),
        },
      });
      setNotice(t('m17.territoryConfig.created'));
      setTerritoryId('');
      setHierarchyLevel('');
      setParentTerritoryId('');
      setReloadKey((k) => k + 1);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <main aria-label="territory-config">
      <h1>{t('m17.territoryConfig.title')}</h1>
      <form onSubmit={onCreate} aria-label="territory-create-form" noValidate>
        <h2>{t('m17.territoryConfig.newTerritory')}</h2>
        {/* G18: territories are chosen by code and name, never typed as ids. */}
        <RecordPicker<TerritoryOption>
          endpoint="/v1/territories?limit=300"
          idField="territoryId"
          label={t('m17.territoryConfig.territoryId')}
          testId="tc-id"
          value={territoryId}
          optionLabel={(o) => named(o.territoryCode, o.territoryName)}
          onPick={(id) => setTerritoryId(id)}
        />
        <label htmlFor="tc-level">{t('m17.territoryConfig.hierarchyLevel')}</label>
        <input id="tc-level" value={hierarchyLevel} onChange={(e) => setHierarchyLevel(e.target.value)} />
        <RecordPicker<TerritoryOption>
          endpoint="/v1/territories?limit=300"
          idField="territoryId"
          label={t('m17.territoryConfig.parentTerritoryId')}
          testId="tc-parent"
          value={parentTerritoryId}
          optionLabel={(o) => named(o.territoryCode, o.territoryName)}
          onPick={(id) => setParentTerritoryId(id)}
        />
        <button type="submit" disabled={saving}>{t('m17.territoryConfig.createBtn')}</button>
      </form>

      {notice ? <p role="status">{notice}</p> : null}
      {error ? <p role="alert">{error}</p> : null}

      {!rows ? <p>{t('m17.territoryConfig.loading')}</p> : (
        <table aria-label="territory-table">
          <thead><tr><th>{t('m17.territoryConfig.colTerritory')}</th><th>{t('m17.territoryConfig.colLevel')}</th><th>{t('m17.territoryConfig.colParent')}</th><th>{t('m17.territoryConfig.colStatus')}</th></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.territoryConfigId ?? r.id ?? i}>
                <td>{named(r.territoryCode, r.territoryName)}</td>
                <td>{r.hierarchyLevel ?? '—'}</td>
                <td>{named(r.parentTerritoryCode, r.parentTerritoryName)}</td>
                <td>{r.status ?? r.recordStatusCode ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
